import { useState, useEffect, useCallback } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Upload, Check, Clock, AlertCircle, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { isValidKycType, mapUploadError, MSG, KYC_MAX_BYTES } from "@/lib/imageUpload";

const ID_TYPES = [
  { key: "aadhaar", label: "Aadhaar", note: "12-digit UIDAI" },
  { key: "passport", label: "Passport", note: "MEA-issued" },
  { key: "driving-license", label: "Driving Licence", note: "RTO-issued" },
];

function StatusPill({ status }) {
  const map = {
    verified: { icon: Check, label: "Verified", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    pending: { icon: Clock, label: "Pending review", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    unverified: { icon: AlertCircle, label: "Not verified", cls: "bg-stone-100 text-stone-600 border-stone-200" },
  };
  const s = map[status] || map.unverified;
  const Icon = s.icon;
  return (
    <span data-testid="kyc-status-pill" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border ${s.cls}`}>
      <Icon size={13} /> {s.label}
    </span>
  );
}

export default function KYC() {
  const [idType, setIdType] = useState("aadhaar");
  const [idNumber, setIdNumber] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [statusInfo, setStatusInfo] = useState({ kyc_status: "unverified", latest_submission: null, rejection_reason: null });
  const nav = useNavigate();
  const { refresh } = useAuth();

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/status");
      setStatusInfo(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!isValidKycType(f)) { toast.error(MSG.docBadType); return; }
    if (f.size > KYC_MAX_BYTES) { toast.error(MSG.docTooLarge); return; }
    setUploadSuccess(false);
    setFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo/PDF of your ID"); return; }
    if (!idNumber.trim()) { toast.error("Enter your ID number"); return; }
    setLoading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("id_type", idType);
      fd.append("id_number", idNumber.trim());
      await api.post("/kyc/upload-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setUploadSuccess(true);
      toast.success("Submitted — an administrator will review shortly.");
      setFile(null);
      setIdNumber("");
      if (preview) { URL.revokeObjectURL(preview); setPreview(null); }
      await Promise.all([loadStatus(), refresh()]);
    } catch (err) {
      toast.error(mapUploadError(err, { doc: true }));
    } finally {
      setLoading(false);
    }
  };

  const status = statusInfo.kyc_status;
  const canResubmit = status === "unverified" || status === "pending";

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="overline">Trust &amp; Verification</div>
            <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Verify your identity.</h1>
          </div>
          <StatusPill status={status} />
        </div>
        <p className="mt-3 text-stone-600">
          Upload a clear photo of your government ID. Our team reviews each submission by hand — usually within one working day.
          The document is stored privately; only administrators and you can view it.
        </p>

        {/* Rejection banner */}
        {status === "unverified" && statusInfo.rejection_reason && (
          <div data-testid="kyc-rejection-banner" className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 flex gap-3">
            <AlertCircle className="text-red-700 mt-0.5 shrink-0" size={20} />
            <div>
              <div className="text-sm font-medium text-red-800">Your previous submission was not approved.</div>
              <div className="text-sm text-red-700 mt-1">Reason: {statusInfo.rejection_reason}</div>
              <div className="text-xs text-red-600 mt-1">Please re-submit with a clearer document.</div>
            </div>
          </div>
        )}

        {/* Pending banner */}
        {status === "pending" && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
            <Clock className="text-amber-700 mt-0.5 shrink-0" size={20} />
            <div>
              <div className="text-sm font-medium text-amber-900">Your submission is under review.</div>
              <div className="text-sm text-amber-800 mt-1">
                An administrator will verify shortly. You will receive an email confirmation once approved.
              </div>
              {statusInfo.latest_submission && (
                <div className="text-xs text-amber-700 mt-1">
                  Submitted {new Date(statusInfo.latest_submission.submitted_at).toLocaleString()} · ID: {statusInfo.latest_submission.id_number_masked}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verified banner */}
        {status === "verified" && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
            <ShieldCheck className="text-emerald-700 mt-0.5 shrink-0" size={20} />
            <div>
              <div className="text-sm font-medium text-emerald-900">You are verified.</div>
              <div className="text-sm text-emerald-800 mt-1">Your profile now shows a Verified badge to prospective matches.</div>
              <button
                data-testid="kyc-continue-btn"
                onClick={() => nav("/dashboard")}
                className="mt-3 btn-pill text-white text-sm px-5 py-2"
                style={{ background: "#1E1B4B" }}
              >
                Return to dashboard
              </button>
            </div>
          </div>
        )}

        {canResubmit && (
          <form onSubmit={submit} className="mt-8 space-y-6">
            {/* ID type */}
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Government ID type</label>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {ID_TYPES.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`kyc-type-${opt.key}`}
                    onClick={() => setIdType(opt.key)}
                    className={`rounded-xl border p-4 text-left transition-colors ${
                      idType === opt.key
                        ? "border-amber-400 bg-amber-50 shadow-sm"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                  >
                    <div className="font-medium text-[#1E1B4B] text-sm">{opt.label}</div>
                    <div className="text-xs text-stone-500 mt-1">{opt.note}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* ID number */}
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">ID number</label>
              <input
                data-testid="kyc-id-number-input"
                required
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.toUpperCase())}
                placeholder={idType === "aadhaar" ? "XXXX XXXX XXXX" : "ID number"}
                className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white focus:border-[#1E1B4B] tracking-wider font-mono"
              />
              <p className="text-xs text-stone-500 mt-1.5">Only the last 4 digits are stored in searchable form. Full number stays with your ID document.</p>
            </div>

            {/* File upload */}
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Upload document photo</label>
              <label
                htmlFor="kyc-file"
                className="mt-2 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-stone-300 hover:border-amber-400 rounded-2xl p-8 bg-white cursor-pointer transition-colors"
              >
                {preview ? (
                  <img src={preview} alt="ID preview" className="max-h-48 rounded-lg" />
                ) : file ? (
                  <div className="flex items-center gap-2 text-stone-700">
                    <FileText size={22} className="text-amber-700" />
                    <span className="text-sm">{file.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload size={28} className="text-amber-700" />
                    <div className="text-sm text-stone-700">Click to choose a file, or drag &amp; drop</div>
                    <div className="text-xs text-stone-500">JPG, PNG, or PDF · up to 10 MB</div>
                  </>
                )}
                <input
                  id="kyc-file"
                  data-testid="kyc-file-input"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={onFileChange}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-stone-500 mt-1.5">Accepted formats: JPG, PNG, PDF. Maximum size: 10 MB.</p>
            </div>

            {loading && (
              <div data-testid="kyc-upload-progress" className="max-w-xs">
                <div className="h-2 w-full rounded-full bg-stone-200 overflow-hidden">
                  <div className="h-full bg-[#1E1B4B] transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                </div>
                <div className="text-xs text-stone-500 mt-1">Uploading… {uploadProgress}%</div>
              </div>
            )}
            {uploadSuccess && !loading && (
              <div data-testid="kyc-upload-success" className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                <CheckCircle2 size={16} className="text-emerald-600" /> Document uploaded successfully.
              </div>
            )}

            <button
              type="submit"
              data-testid="kyc-submit-btn"
              disabled={loading || !file || !idNumber.trim()}
              className="btn-pill inline-flex items-center gap-2 text-white px-8 py-3 disabled:opacity-40"
              style={{ background: "#1E1B4B" }}
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {loading ? "Uploading…" : status === "pending" ? "Replace submission" : "Submit for review"}
            </button>

            <p className="text-xs text-stone-500 italic">
              Your document is stored privately. It is only accessible to you and to Anubandh administrators for verification purposes.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
